import { computed, ComputedRef } from '@vue/composition-api';
import { tryOnUnmounted } from '@vueuse/shared';

type CacheKey = any;
type MemorizeCache<Key, Value> = {
  get: (key: Key) => ComputedRef<Value> | undefined;
  set: (key: Key, value: ComputedRef<Value>) => void;
  has: (key: Key) => boolean;
  clear: () => void;
};
export type UseComputedOptions<T, Args extends unknown[]> = {
  cache?: MemorizeCache<CacheKey, T>;
  getKey?: (...args: Args) => CacheKey;
};

function getMapCache<Value>(): MemorizeCache<CacheKey, Value> {
  const data = new Map<CacheKey, ComputedRef<Value>>();
  return {
    get: key => data.get(key),
    set: (key, value) => data.set(key, value),
    has: key => data.has(key),
    clear: () => {
      data.clear();
    }
  };
}

/**
 * Creates a computed value that is memoized based on the arguments passed to the getter function.
 * @param getter The getter function to memoize.
 * @param options Options for the computed value.
 * @returns A function that returns a computed value based on the arguments passed to the getter function.
 */
export function useComputed<T, Args extends unknown[]>(getter: (...args: Args) => T, options?: UseComputedOptions<T, Args>) {
  const cache = options?.cache || getMapCache<T>();
  const generateKey = (...args: Args) => options?.getKey?.(...args) || JSON.stringify(args);
  tryOnUnmounted(() => {
    cache.clear();
  });
  return (...args: Args) => {
    const key = generateKey(...args);
    if (cache.has(key)) {
      return cache.get(key)!;
    }
    const value = computed(() => getter(...args));
    cache.set(key, value);
    return value;
  };
}
